/**
 * Event Details contact roster: filter, CSV export, and print helpers.
 * Columns match the approved Event Details contact-roster requirements.
 */
import dayjs from "dayjs";
import { escapeCsvCell } from "./csvFileHelpers";
import { formatPhoneDisplay } from "./phoneFormat";
import { formatEventAttendanceStatusLabel } from "./eventRegistrationDisplay";
import { isPlaceholderEmail } from "./displayEmail";

export const CONTACT_ROSTER_STATUS = {
  REGISTERED: "registered",
  WAITLISTED: "waitlisted",
  CANCELLED: "cancelled",
};

export const CONTACT_ROSTER_CSV_HEADER_LABELS = [
  "Participant",
  "Status",
  "Payment",
  "Grade",
  "School",
  "Email",
  "Phone",
  "Parent name",
  "Parent email",
  "Parent phone",
];

export const CONTACT_ROSTER_CSV_HEADER_LINE = CONTACT_ROSTER_CSV_HEADER_LABELS.join(",");

/** @param {unknown} attendance */
export function getContactRosterStatusBucket(attendance) {
  const att = (attendance != null ? String(attendance) : "").trim();
  if (att === "Waitlisted") return CONTACT_ROSTER_STATUS.WAITLISTED;
  if (att === "Cancelled") return CONTACT_ROSTER_STATUS.CANCELLED;
  return CONTACT_ROSTER_STATUS.REGISTERED;
}

/**
 * @param {Record<string, unknown>[]} participants
 * @returns {{ registered: number, waitlisted: number, cancelled: number }}
 */
export function countContactRosterByStatus(participants) {
  const counts = { registered: 0, waitlisted: 0, cancelled: 0 };
  (Array.isArray(participants) ? participants : []).forEach((p) => {
    const bucket = getContactRosterStatusBucket(p?.attendance);
    counts[bucket] += 1;
  });
  return counts;
}

/**
 * @param {Record<string, unknown>[]} participants
 * @param {{ includeRegistered?: boolean, includeWaitlisted?: boolean, includeCancelled?: boolean, selectedIds?: Set<string>|string[]|null }} opts
 * @returns {Record<string, unknown>[]}
 */
export function filterContactRosterParticipants(participants, opts = {}) {
  const list = Array.isArray(participants) ? participants : [];
  const include = {
    [CONTACT_ROSTER_STATUS.REGISTERED]: !!opts.includeRegistered,
    [CONTACT_ROSTER_STATUS.WAITLISTED]: !!opts.includeWaitlisted,
    [CONTACT_ROSTER_STATUS.CANCELLED]: !!opts.includeCancelled,
  };
  let selected = null;
  if (opts.selectedIds instanceof Set) {
    selected = opts.selectedIds.size > 0 ? opts.selectedIds : null;
  } else if (Array.isArray(opts.selectedIds) && opts.selectedIds.length > 0) {
    selected = new Set(opts.selectedIds.map((id) => String(id)));
  }

  return list.filter((p) => {
    const bucket = getContactRosterStatusBucket(p?.attendance);
    if (!include[bucket]) return false;
    if (selected && !selected.has(String(p?._id))) return false;
    return true;
  });
}

function displayName(p) {
  const name = [p?.first_name, p?.last_name]
    .filter(Boolean)
    .map((s) => String(s).trim())
    .join(" ")
    .trim();
  return name || "";
}

function cellText(value) {
  if (value == null) return "";
  return String(value).trim();
}

/** Hide system placeholder emails in export/print (keep real addresses). */
function displayEmail(email) {
  const s = cellText(email);
  if (!s || isPlaceholderEmail(s)) return "";
  return s;
}

/**
 * @param {Record<string, unknown>} row
 * @returns {string[]}
 */
export function contactRosterRowToCsvCells(row) {
  return [
    displayName(row),
    formatEventAttendanceStatusLabel(row?.attendance),
    // Match Event Participants table: payment_status || 'NA'
    cellText(row?.payment_status) || "NA",
    cellText(row?.grade),
    cellText(row?.school),
    displayEmail(row?.email),
    row?.phone ? formatPhoneDisplay(row.phone) : "",
    cellText(row?.parent_name),
    displayEmail(row?.parent_email),
    row?.parent_phone ? formatPhoneDisplay(row.parent_phone) : "",
  ];
}

/**
 * @param {Record<string, unknown>[]} rows
 * @returns {string}
 */
export function buildContactRosterCsvExportText(rows) {
  const list = Array.isArray(rows) ? rows : [];
  const lines = [
    CONTACT_ROSTER_CSV_HEADER_LINE,
    ...list.map((row) => contactRosterRowToCsvCells(row).map(escapeCsvCell).join(",")),
  ];
  return lines.join("\r\n");
}

/**
 * @param {string} name
 * @returns {string}
 */
export function buildContactRosterCsvFilename(eventName) {
  const raw = eventName != null ? String(eventName).trim() : "";
  const slug = raw
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);
  const datePart = dayjs().format("YYYY-MM-DD");
  return `event-participants-${slug || "event"}-${datePart}.csv`;
}

function escapeHtml(value) {
  return String(value == null ? "" : value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/**
 * @param {{ event?: Record<string, unknown>|null, rows: Record<string, unknown>[] }} args
 * @returns {string}
 */
export function buildContactRosterPrintHtml({ event = null, rows = [] }) {
  const list = Array.isArray(rows) ? rows : [];
  const eventName = cellText(event?.name) || "Event";
  const location = cellText(event?.location);
  const startD = event?.start_datetime ? dayjs(event.start_datetime) : null;
  const startDisplay = startD && startD.isValid() ? startD.format("MM/DD/YYYY h:mm A") : "";
  const headerBits = [startDisplay, location].filter(Boolean).join(" · ");

  const bodyRows = list
    .map((row) => {
      const cells = contactRosterRowToCsvCells(row).map((c) => `<td>${escapeHtml(c)}</td>`);
      return `<tr>${cells.join("")}</tr>`;
    })
    .join("");

  const headers = CONTACT_ROSTER_CSV_HEADER_LABELS.map((h) => `<th>${escapeHtml(h)}</th>`).join("");

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(eventName)} — Roster</title>
  <style>
    body { font-family: Arial, Helvetica, sans-serif; font-size: 12px; color: #212529; margin: 16px; }
    h1 { font-size: 18px; margin: 0 0 4px 0; }
    .meta { color: #495057; margin: 0 0 12px 0; }
    table { width: 100%; border-collapse: collapse; }
    th, td { border: 1px solid #dee2e6; padding: 4px 6px; text-align: left; vertical-align: top; }
    th { background: #f8f9fa; font-weight: 600; }
    @media print {
      body { margin: 0; }
    }
  </style>
</head>
<body>
  <h1>${escapeHtml(eventName)}</h1>
  ${headerBits ? `<p class="meta">${escapeHtml(headerBits)}</p>` : ""}
  <table>
    <thead><tr>${headers}</tr></thead>
    <tbody>${bodyRows || `<tr><td colspan="${CONTACT_ROSTER_CSV_HEADER_LABELS.length}">No participants</td></tr>`}</tbody>
  </table>
</body>
</html>`;
}

/**
 * Opens a print window for the contact roster. Returns false if the popup was blocked.
 * @param {{ event?: Record<string, unknown>|null, rows: Record<string, unknown>[] }} args
 * @returns {boolean}
 */
export function printContactRoster(args) {
  const html = buildContactRosterPrintHtml(args);
  const win = window.open("", "_blank");
  if (!win) return false;
  win.document.open();
  win.document.write(html);
  win.document.close();
  win.focus();
  // Allow layout before print dialog
  setTimeout(() => {
    try {
      win.print();
    } catch {
      /* ignore */
    }
  }, 100);
  return true;
}
